import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface SvgSanitizationOptions {
  allowScripts?: boolean;
  allowExternalResources?: boolean;
  allowDataUrls?: boolean;
  maxSize?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SvgSanitizerService {
  private readonly defaultOptions: SvgSanitizationOptions = {
    allowScripts: false,
    allowExternalResources: false,
    allowDataUrls: false,
    maxSize: 15000, // 15KB max
  };

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Sanitizes SVG content using Angular's built-in sanitization
   * with additional SVG-specific security measures
   */
  sanitizeSvg(
    svgContent: string,
    options: SvgSanitizationOptions = {},
  ): SafeHtml {
    const opts = { ...this.defaultOptions, ...options };
    try {
      // Basic validation
      if (!this.isValidSvgContent(svgContent, opts)) {
        return this.getSafeFallbackSvg();
      }
      // Pre-process the SVG to remove dangerous content
      const cleanedSvg = this.preprocessSvg(svgContent, opts);

      // For SVG content, we rely primarily on our custom sanitization
      // Angular's HTML sanitizer is too restrictive for SVG elements
      // We've already removed dangerous content in preprocessSvg()
      return this.sanitizer.bypassSecurityTrustHtml(cleanedSvg);
    } catch (error) {
      console.warn('SVG sanitization failed:', error);
      return this.getSafeFallbackSvg();
    }
  }

  /**
   * Validates SVG content before sanitization
   */
  private isValidSvgContent(
    svgContent: string,
    options: SvgSanitizationOptions,
  ): boolean {
    if (!svgContent || typeof svgContent !== 'string') {
      return false;
    }

    // Check size limit
    if (options.maxSize && svgContent.length > options.maxSize) {
      return false;
    }

    // Check for dangerous patterns
    const dangerousPatterns = [
      /<script[^>]*>[\s\S]*?<\/script>/gi,
      /javascript:/gi,
      /vbscript:/gi,
      /data:text\/html/gi,
      /on\w+\s*=/gi, // Event handlers
      /<iframe/gi,
      /<object/gi,
      /<embed/gi,
      /<link/gi,
      /<meta/gi,
      /expression\s*\(/gi,
      /url\s*\(\s*javascript:/gi, // Only block javascript URLs, not all URLs
    ];

    return !dangerousPatterns.some((pattern) => pattern.test(svgContent));
  }

  /**
   * Pre-processes SVG content to remove dangerous elements
   */
  private preprocessSvg(
    svgContent: string,
    options: SvgSanitizationOptions,
  ): string {
    let cleaned = svgContent;

    // Remove script tags and event handlers
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    cleaned = cleaned.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Remove dangerous elements
    const dangerousElements = [
      'script',
      'iframe',
      'object',
      'embed',
      'link',
      'meta',
      'style',
    ];
    dangerousElements.forEach((tag) => {
      // eslint-disable-next-line no-useless-escape
      const regex = new RegExp(`<${tag}[^>]*>[\s\S]*?<\/${tag}>`, 'gi');
      cleaned = cleaned.replace(regex, '');
    });

    // Remove dangerous attributes - be more specific to avoid removing valid SVG content
    cleaned = cleaned.replace(/javascript:/gi, '');
    cleaned = cleaned.replace(/vbscript:/gi, '');
    cleaned = cleaned.replace(/data:text\/html/gi, '');
    cleaned = cleaned.replace(/url\s*\(\s*javascript:/gi, ''); // Remove javascript URLs

    // Remove expression() functions in CSS
    cleaned = cleaned.replace(/expression\s*\([^)]*\)/gi, '');

    // Remove any remaining event handlers
    cleaned = cleaned.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Remove external resource references if not allowed
    if (!options.allowExternalResources) {
      // Only remove external URLs, not internal ones like #clipPath
      cleaned = cleaned.replace(/xlink:href\s*=\s*["'][^#][^"']*["']/gi, '');
      cleaned = cleaned.replace(/href\s*=\s*["'][^#][^"']*["']/gi, '');
    }

    // Remove data URLs if not allowed
    if (!options.allowDataUrls) {
      cleaned = cleaned.replace(/data:[^"'\s>]+/gi, '');
    }

    // Clean up any empty attributes that might have been left behind
    cleaned = cleaned.replace(/\s+=\s*["']\s*["']/gi, '');
    cleaned = cleaned.replace(/\s+=\s*["']\s*["']/gi, '');

    return cleaned;
  }

  /**
   * Returns a safe fallback SVG
   */
  private getSafeFallbackSvg(): SafeHtml {
    const fallbackSvg = `
      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
      </svg>
    `;
    return this.sanitizer.bypassSecurityTrustHtml(fallbackSvg);
  }

  /**
   * Validates SVG structure without sanitizing
   */
  isValidSvg(svgContent: string): boolean {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');

      // Check for parsing errors
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        return false;
      }

      // Check if root element is SVG
      const svgElement = doc.documentElement;
      return svgElement.tagName.toLowerCase() === 'svg';
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets SVG dimensions safely
   */
  getSvgDimensions(
    svgContent: string,
  ): { width: number; height: number } | null {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svgElement = doc.documentElement;

      if (svgElement.tagName.toLowerCase() !== 'svg') {
        return null;
      }

      const viewBox = svgElement.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(/\s+/);
        if (parts.length >= 4) {
          return {
            width: parseFloat(parts[2]) || 24,
            height: parseFloat(parts[3]) || 24,
          };
        }
      }

      const width = parseFloat(svgElement.getAttribute('width') || '24');
      const height = parseFloat(svgElement.getAttribute('height') || '24');

      return { width, height };
    } catch (error) {
      return null;
    }
  }
}
